import type { ParsedUrlQuery } from 'querystring';
import { TimePreset } from '../../../schema/hannibal/types.generated';
import { parseToEnum } from '../../../util/parseToEnum';
import { FilterChoice } from '../types/FilterChoice';
import { GenderChoice } from '../types/GenderChoice';

export const makeQueryParams = (filters: FilterChoice): Record<string, string | undefined> => {
  /** 'after-hours' already has a special place in the URL path, so it doesn't get produced as part
   * of the query params.
   * @see ./path-segments/wholePath
   */
  const time =
    filters.onlineAppointments?.time === TimePreset.AfterHours
      ? undefined
      : filters.onlineAppointments?.time;

  const queryParams = {
    ...filters.extraQuery,
    acceptsNewPatients: filters.acceptsNewPatients ? 'true' : undefined,
    fluVaxAppointmentType: filters.fluVaxAppointmentType ? 'true' : undefined,
    gender: filters.gender || undefined,
    governmentScheme: filters.governmentScheme || undefined,
    hideTeleAggregators: filters.hideTeleAggregators ? 'true' : undefined,
    insurance: filters.insurance,
    insuranceProgram: filters.insuranceProgram,
    language: filters.language || undefined,
    paymentMethod: filters.paymentMethod,
    pricingAvailable: filters.pricingAvailable ? 'true' : undefined,
    search: filters.text || undefined,
    specialOffer: filters.specialOffer ? 'true' : undefined,
    time: time,
    vaccineType: filters.vaccineType,
    withFilters: filters.alwaysShowFilters ? 'true' : undefined,
  };

  return queryParams;
};

export const parseQueryParamsToFilters = (query: ParsedUrlQuery, filters: FilterChoice) => {
  const {
    acceptsNewPatients,
    fluVaxAppointmentType,
    gender,
    governmentScheme,
    hideTeleAggregators,
    insurance,
    insuranceProgram,
    language,
    paymentMethod,
    pricingAvailable,
    search,
    slugs: _ignored,
    specialOffer,
    time,
    vaccineType,
    withFilters,
    ...extraQuery
  } = query;

  const parsedTime = time ? parseToEnum(String(time), TimePreset) : undefined;

  const onlineAppointments = parsedTime
    ? { ...filters.onlineAppointments, time: parsedTime }
    : filters.onlineAppointments;

  const parsedFilters: FilterChoice = {
    acceptsNewPatients: Boolean(acceptsNewPatients),
    alwaysShowFilters: Boolean(withFilters),
    extraQuery: extraQuery,
    fluVaxAppointmentType: Boolean(fluVaxAppointmentType),
    gender:
      gender === GenderChoice.MALE || gender === GenderChoice.FEMALE ? gender : filters.gender,
    governmentScheme: governmentScheme ? String(governmentScheme) : filters.governmentScheme,
    hideTeleAggregators: Boolean(hideTeleAggregators),
    insurance: insurance ? String(insurance) : filters.insurance,
    insuranceProgram: insuranceProgram ? String(insuranceProgram) : filters.insuranceProgram,
    language: language ? String(language) : filters.language,
    onlineAppointments: onlineAppointments,
    paymentMethod: paymentMethod ? String(paymentMethod) : filters.paymentMethod,
    pricingAvailable: Boolean(pricingAvailable),
    specialOffer: Boolean(specialOffer),
    text: search && String(search),
    vaccineType: vaccineType ? String(vaccineType) : filters.vaccineType,
  };

  Object.assign(filters, parsedFilters);
};
